import React, { useState, useEffect } from 'react';
import { PublicHeader } from '../../components/public/PublicHeader';
import { PublicFooter } from '../../components/public/PublicFooter';
import { StargazeImage } from '../../components/common/StargazeImage';
import { Sparkles, Calendar, Award, Film, ArrowRight, CheckCircle2, ChevronRight } from 'lucide-react';
import { getSiteContent } from '../../lib/cmsService';
import { SiteContentConfig } from '../../types';

interface ExperienceHighlight {
  id: string;
  title: string;
  category: string;
  tagline: string;
  description: string;
  imageUrl: string;
  dateOrFrequency: string;
  highlights: string[];
}

const DEFAULT_EXPERIENCES: ExperienceHighlight[] = [
  {
    id: 'exp-1',
    title: 'RED CARPET PREMIERES & FESTIVAL SCREENINGS',
    category: 'VIP THEATRICAL',
    tagline: 'Exclusive Galas & Global Festival Circuits',
    description: 'Curated world premiere galas, red carpet presentations, and film festival showcases featuring directors, cast, and industry delegates.',
    imageUrl: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80',
    dateOrFrequency: 'Seasonal Theatrical Releases',
    highlights: ['DCP 4K & IMAX Projection', 'Talent Q&A Sessions', 'Press & Media Photowalls'],
  },
  {
    id: 'exp-2',
    title: 'CINEMATOGRAPHY & DIRECTING MASTERCLASSES',
    category: 'ACADEMY SESSIONS',
    tagline: 'Hands-on Workshops with Veteran Cine Technicians',
    description: 'Intimate technical masterclasses exploring large-format cinema camera workflows, anamorphic optics, lighting geometry, and visual storytelling.',
    imageUrl: 'https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80',
    dateOrFrequency: 'Quarterly Industry Sessions',
    highlights: ['ARRI & Sony Venice Workflows', 'Live Color Grading Demos', 'Hands-on Rig Operating'],
  },
  {
    id: 'exp-3',
    title: 'LIVE MUSIC CONCERT TOURS & SHOWCASES',
    category: 'LIVE ACTIVATION',
    tagline: 'Headline Musical Productions & Stage Shows',
    description: 'Immersive live music concert tours, orchestral screenings, and multimedia spectacles produced with concert-grade staging and cinematic visuals.',
    imageUrl: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
    dateOrFrequency: 'Nationwide Tours',
    highlights: ['Full Arena Acoustic Design', 'Integrated LED Visual Walls', 'Multi-Camera 4K Live Broadcast'],
  },
  {
    id: 'exp-4',
    title: 'IMMERSIVE SPATIAL & BRAND EXPERIENCES',
    category: 'SPATIAL ACTIVATIONS',
    tagline: 'Interactive Exhibitions & Film Installations',
    description: 'Custom pop-up exhibitions, 360-degree spatial storytelling pavilions, and experiential launch events for films, brands, and cultural institutions.',
    imageUrl: 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?auto=format&fit=crop&w=1200&q=80',
    dateOrFrequency: 'Bespoke Commissions',
    highlights: ['Spatial Audio Environments', 'Interactive Set Pieces', 'VIP Hospitality Suites'],
  },
];

export const ExperiencesPage: React.FC = () => {
  const [siteContent, setSiteContent] = useState<SiteContentConfig | null>(null);

  useEffect(() => {
    let isMounted = true;
    getSiteContent().then((data) => {
      if (isMounted && data) setSiteContent(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const experiencesConfig = siteContent?.homepageSections?.experiences;

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#18181B] font-sans selection:bg-[#D97706] selection:text-white">
      <PublicHeader />

      <main className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Page Header */}
        <div className="border-b border-zinc-200 pb-10">
          <div className="inline-flex items-center gap-2 text-[#B45309] font-mono text-xs font-bold tracking-widest uppercase mb-3">
            <Sparkles className="w-4 h-4 text-[#D97706]" />
            <span>05 // LIVE EXPERIENCES & ACTIVATIONS</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-black font-serif-cinematic text-zinc-900 tracking-tight uppercase">
            {experiencesConfig?.heading || 'FAN EXPERIENCES & SCREENINGS'}
          </h1>
          <p className="text-zinc-600 text-sm sm:text-base mt-3 max-w-3xl leading-relaxed">
            {experiencesConfig?.description ||
              'Director masterclasses, VIP festival screenings, red carpet galas, and bespoke audio-visual activations produced by Stargaze Media.'}
          </p>
        </div>

        {/* Featured Experiences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {DEFAULT_EXPERIENCES.map((exp) => (
            <div
              key={exp.id}
              className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-[#D97706] hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
            >
              <div className="aspect-[16/10] overflow-hidden relative bg-zinc-900">
                <StargazeImage
                  src={exp.imageUrl}
                  alt={exp.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <span className="absolute top-4 left-4 bg-black/60 backdrop-blur-md text-amber-400 border border-white/10 font-mono text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                  {exp.category}
                </span>
                <span className="absolute bottom-4 right-4 text-xs font-mono text-zinc-300">
                  {exp.dateOrFrequency}
                </span>
              </div>

              <div className="p-6 sm:p-8 space-y-4 flex-1 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] font-mono text-[#B45309] font-bold uppercase tracking-widest block mb-1">
                    {exp.tagline}
                  </span>
                  <h3 className="text-2xl font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition">
                    {exp.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-zinc-600 font-sans leading-relaxed mt-2">
                    {exp.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-zinc-100 space-y-2">
                  <div className="text-[11px] font-mono text-zinc-500 uppercase font-bold tracking-wider">
                    Key Features:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {exp.highlights.map((h, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-900 border border-amber-500/20 text-xs font-mono"
                      >
                        <CheckCircle2 className="w-3 h-3 text-[#D97706]" />
                        <span>{h}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-sm">
          <span className="text-xs font-mono text-[#B45309] font-bold uppercase tracking-widest block">
            CUSTOM ACTIVATIONS
          </span>
          <h2 className="text-3xl sm:text-4xl font-black font-serif-cinematic text-zinc-900 uppercase">
            PRODUCE AN EXPERIENCE WITH STARGAZE
          </h2>
          <p className="text-zinc-600 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            From red carpet theatrical premieres and cultural festivals to high-end audio-visual installations, our production teams deliver memorable brand and audience experiences.
          </p>
          <div className="pt-2">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#D97706] hover:bg-[#B45309] text-white font-mono font-bold text-xs uppercase tracking-widest transition shadow-lg shadow-amber-600/20 hover:scale-105"
            >
              <span>CONNECT WITH EXPERIENCES TEAM</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};
